import { FunctionalComponent, h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Localizer } from 'preact-i18n';
import * as appStyles from '../../../../assets/global.scss';
import { DASH, NBSP, PreviousSearch, PREVIOUS_SEARCHES_KEY, Properties, Location } from '../../../../common';
import { actions, Actions, BookingState, connectWithStyles } from '../../../../store';
import { WithStyles } from '../../../../style';
import Icon from '../../../../common/Icon/Icon';
import { isNotPresent, stopPropagation } from '../../../../utils';
import { DateFormat, DateText, FinText } from '../../../../i18n';
import { storage } from '../../../../store/storage';
import { EnvConfig } from '../../../../config';
import { getLocations, Response, Lock } from '../../../../services';
import * as styles from './PreviousSearches.scss';
import { getLang } from '../../../../i18n/getLang';
import { searchIsValid } from '../../locationUtils';
import { LocationCode } from '../LocationCode/LocationCode';

export interface PreviousSearchesProps extends Properties {
  onModalClose: () => void;
  onDelete: (prevSearchesLenth: number) => void;
  activeItem: number;
}

interface StoreProps {
  locale: string;
  envConfig: EnvConfig;
  enableCompactWidget: boolean;
}

type EffectiveProps = PreviousSearchesProps & StoreProps & Actions & WithStyles;

const mapStateToProps = ({ selection: { locale }, envConfig, enableCompactWidget }: BookingState): StoreProps => ({
  locale,
  envConfig,
  enableCompactWidget,
});

const getLocationsLocks: { [key: string]: Lock } = {};

export const PreviousSearches: FunctionalComponent<PreviousSearchesProps> = connectWithStyles<PreviousSearchesProps>(
  [styles, appStyles],
  {
    mapStateToProps,
    actions,
  }
)(
  ({
    onModalClose,
    onDelete,
    setSelection,
    logGtmUiEvent,
    tx,
    envConfig,
    locale,
    activeItem,
    enableCompactWidget,
  }: EffectiveProps) => {
    const [previousSearches, setPreviousSearches] = useState([]);

    useEffect(() => {
      const initialSearches = (storage.local.get<PreviousSearch[]>(PREVIOUS_SEARCHES_KEY) ?? []).filter(searchIsValid);

      const selectedLang = getLang(locale);

      if (locale && initialSearches.some((search) => search.locale && getLang(search.locale) !== selectedLang)) {
        const localizedSearches = initialSearches.map((search) => {
          if (search.locale && getLang(search.locale) === selectedLang) {
            return Promise.resolve(search);
          }

          const lockKey = `${search.origin}-${search.destination}`;
          if (!getLocationsLocks[lockKey]) {
            getLocationsLocks[lockKey] = new Lock();
          }

          return getLocations(
            envConfig,
            [
              {
                origin: search.origin,
                destination: search.destination,
              },
            ],
            locale,
            getLocationsLocks[lockKey]
          ).then(([originResponse, destinationResponse]: Response<Location>[]) => {
            return {
              ...search,
              origin: {
                ...search.origin,
                cityName: originResponse?.payload?.cityName || search.origin.cityName,
              },
              destination: {
                ...search.destination,
                cityName: destinationResponse?.payload?.cityName || search.destination.cityName,
              },
              locale,
            };
          });
        });

        Promise.all(localizedSearches).then((searches) => {
          storage.local.set(PREVIOUS_SEARCHES_KEY, searches);
          setInitialSearches(searches);
        });
      } else {
        setInitialSearches(initialSearches);
      }
    }, []);

    const setInitialSearches = (searches: PreviousSearch[]) => {
      setPreviousSearches(searches);
      if (searches.length > 0) {
        logGtmUiEvent({ id: 'show-previous-searches', state: `previous-searches-${searches.length}` });
      }
    };

    const fillSelections = (search: PreviousSearch, event: Event): void => {
      stopPropagation(event);
      setSelection(search);
      logGtmEvent('select', search);
      onModalClose();
    };

    const logGtmEvent = (type: string, search: PreviousSearch): void => {
      const { origin, destination, travelDates } = search;
      const isOneway = isNotPresent(travelDates.returnDate);
      const locations = `${origin.locationCode} - ${destination.locationCode}`;
      const dates = `DEPARTURE: ${travelDates.departureDate}${!isOneway ? `, RETURN: ${travelDates.returnDate}` : ''}`;

      logGtmUiEvent({
        id: `${type}-previous-search`,
        state: `${locations}, ${dates}`,
      });
    };

    const removeSearch = (index: number, event: Event): void => {
      stopPropagation(event);
      const newSearches = [...previousSearches.slice(0, index), ...previousSearches.slice(index + 1)];
      storage.local.set(PREVIOUS_SEARCHES_KEY, newSearches);
      logGtmEvent('remove', previousSearches[index]);
      setPreviousSearches(newSearches);
      onDelete(newSearches.length);
    };

    return (
      <Fragment>
        {previousSearches.length > 0 && (
          <Fragment>
            <div class={tx('grey-50-bg rounded-small ps-large-x ps-medium-y border-bottom')}>
              <h3 class="font-body-1 nordic-blue-900-text">
                <FinText id={'locationModal.previousSearches'} />
              </h3>
            </div>
            <ul class="no-list-style">
              {previousSearches.map((search, index) => {
                return (
                  <li
                    key={index}
                    tabIndex={0}
                    onClick={(e) => fillSelections(search, e)}
                    data-test-previous-search-item
                    class={tx('ps-large-x', {
                      'nordic-blue-50-bg nordic-blue-50-border': activeItem === index,
                    })}
                  >
                    <div
                      class={tx('flex flex--space-between rounded-bottom-left cursor-pointer ps-medium-y', {
                        'border-bottom': index !== previousSearches.length - 1 && activeItem !== index,
                      })}
                    >
                      <div class={tx('ps-min-width')}>
                        <div class="nordic-blue-900-text">
                          {search.origin.cityName}
                          <LocationCode
                            type={search.origin.type}
                            locationCode={search.origin.locationCode}
                            enableCompactWidget={enableCompactWidget}
                          />
                          – {search.destination.cityName}
                          <LocationCode
                            type={search.destination.type}
                            locationCode={search.destination.locationCode}
                            enableCompactWidget={enableCompactWidget}
                          />
                        </div>
                        <div class="nordic-blue-900-text font-body-1">
                          <DateText value={search.travelDates.departureDate} format={DateFormat.DATE} />
                          {search.travelDates.returnDate && [
                            DASH,
                            NBSP,
                            <DateText
                              key={search.travelDates.returnDate}
                              value={search.travelDates.returnDate}
                              format={DateFormat.DATE}
                            />,
                          ]}
                        </div>
                      </div>
                      <div class="flex--nogrow flex--self-center nordic-blue-900-text pr-xsmall-l">
                        <Localizer>
                          <Icon
                            tabIndex={0}
                            aria-label={<FinText id="action.remove" />}
                            iconName="trash"
                            class={tx('is-xsmall cursor-pointer')}
                            onClick={(e) => removeSearch(index, e)}
                          />
                        </Localizer>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Fragment>
        )}
      </Fragment>
    );
  }
);
